import PropTypes from 'prop-types';
import React from 'react';

import BioentitySearchInput from 'inventory/BioentitySearchInput';
import {MAX_NEW_ROW_COUNT, MIN_NEW_ROW_COUNT} from 'notebook/data-tables/DataTableConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import BatchSearchInput from 'samples/batches/BatchSearchInput';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';
import {NumericInputChangeEvent} from 'util/Types';

interface StandaloneDataTableAddSampleModalProps {
  linkType: string;
  targetSchemaId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: (sample: {id: string}, numberOfRows: number) => void;
}

interface State {
  selectedSample: {id: string} | null;
  numberOfRows: number | null;
}

export default class StandaloneDataTableAddSampleModal extends React.Component<
  StandaloneDataTableAddSampleModalProps,
  State
> {
  static propTypes = {
    linkType: PropTypes.oneOf([FIELD_TYPES.BATCH_LINK.type, FIELD_TYPES.BIOENTITY_LINK.type]).isRequired,
    targetSchemaId: PropTypes.string,
    onRequestClose: PropTypes.func.isRequired,
    onRequestSubmit: PropTypes.func.isRequired,
  };

  state: State = {
    selectedSample: null,
    numberOfRows: 1,
  };

  _handleSubmit = (): void => {
    this.props.onRequestSubmit(this.state.selectedSample!, this.state.numberOfRows!);
  };

  _handleChangeSample = (samples: Array<{id: string}>): void => {
    this.setState({selectedSample: samples.length ? samples[0] : null});
  };

  _handleUpdateRowCount = (e: NumericInputChangeEvent): void => {
    this.setState({
      numberOfRows: e.target.numericValue,
    });
  };

  _hasInvalidRowCount(): boolean {
    const {numberOfRows} = this.state;
    return (
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      numberOfRows === null ||
      !Number.isInteger(numberOfRows) ||
      numberOfRows < MIN_NEW_ROW_COUNT ||
      numberOfRows > MAX_NEW_ROW_COUNT
    );
  }

  render(): JSX.Element {
    const sampleType = this.props.linkType === FIELD_TYPES.BATCH_LINK.type ? 'Batch' : 'Entity';
    return (
      <Modal
        title={`Add ${sampleType} to ${MODEL_NAMES.Result_Table}`}
        onRequestClose={this.props.onRequestClose}
        buttons={[
          {
            text: 'Add',
            style: ButtonStyle.PRIMARY,
            onClick: this._handleSubmit,
            disabled: !this.state.selectedSample || this._hasInvalidRowCount(),
            dataTestAttrs: {component: 'StandaloneDataTableAddSampleModal', element: 'addButton'},
          },
        ]}
      >
        <Row>
          <Col xs={8}>
            <FieldLabel label={sampleType} />
            {this.props.linkType === FIELD_TYPES.BATCH_LINK.type ? (
              <BatchSearchInput
                autoFocus={true}
                onChange={this._handleChangeSample}
                selectedBatches={this.state.selectedSample ? [this.state.selectedSample] : []}
                shouldAllowMultiple={false}
                batchSchemaId={this.props.targetSchemaId}
              />
            ) : (
              <BioentitySearchInput
                autoFocus={true}
                onChange={this._handleChangeSample}
                selectedItems={this.state.selectedSample ? [this.state.selectedSample] : []}
                shouldAllowMultiple={false}
                tagSchemaOrInterfaceId={this.props.targetSchemaId}
              />
            )}
          </Col>
          <Col xs={4}>
            <FieldLabel label="Number of rows" />
            <div className="tallInput">
              <NumericInput
                className="tallInput-input"
                min={MIN_NEW_ROW_COUNT}
                max={MAX_NEW_ROW_COUNT}
                onChange={this._handleUpdateRowCount}
                value={this.state.numberOfRows}
              />
            </div>
          </Col>
        </Row>
      </Modal>
    );
  }
}
